import { useAtom } from '@reatom/npm-react'
import { AnimatePresence } from 'motion/react'
import { useMemo, useState } from 'react'
import { useParams } from 'react-router'
import styled from 'styled-components'

import { useHapticFeedback } from '@blade/ui-kit'
import { reatomPremiumCurrencyInfo } from '@root/features/premium/currency-information'
import { useTranslation } from '@root/shared/localization'
import { FadeOverflow } from '@root/shared/ui-kit'
import { PremiumCurrencyDetails } from '@root/widgets/premium/premium-currency-details/ui/common'
import { PremiumHeader } from '@root/widgets/premium/premium-header'
import { UploadPremiumBannerDrawer } from '@root/widgets/premium/update-banner-drawer/ui/mobile'
import { UploadPremiumEmojiDrawer } from '@root/widgets/premium/update-emoji-drawer'
import { UploadPremiumLogoDrawer } from '@root/widgets/premium/update-logo-drawer/ui/mobile'

import type { CurrencyAddress } from '@root/entities/currency/model/CurrencyAddress'

type Props = {}

export const PremiumCurrencyPage: React.FC<Props> = () => {
  const { t } = useTranslation()

  const { currency } = useParams<{ currency: CurrencyAddress }>()
  const model = useMemo(
    // eslint-disable-next-line @reatom/unit-naming-rule
    () => reatomPremiumCurrencyInfo({ currencyAddress: currency! }),
    [currency],
  )

  const haptics = useHapticFeedback()
  const [pairState] = useAtom(model.pairState)

  const [editImageOpen, setEditImageOpen] = useState(false)
  const [editBannerOpen, setEditBannerOpen] = useState(false)
  const [emojiEditOpen, setEditEmojiOpen] = useState(false)

  return (
    <Root>
      <PremiumHeader title={t('premium.currency.title')} />

      <Overflow variant="vertical">
        <PremiumCurrencyDetails
          model={model}
          onEmojiEdit={() => {
            haptics.impactOccurred('light')
            setEditEmojiOpen(true)
          }}
          onLogoEdit={() => {
            haptics.impactOccurred('light')
            setEditImageOpen(true)
          }}
          onBannerEdit={() => {
            haptics.impactOccurred('light')
            setEditBannerOpen(true)
          }}
        />

        {pairState.value?.pair ? (
          <>
            <UploadPremiumLogoDrawer
              open={editImageOpen}
              onOpenChange={setEditImageOpen}
              pair={pairState.value.pair}
            />
            <UploadPremiumBannerDrawer
              open={editBannerOpen}
              onOpenChange={setEditBannerOpen}
              pair={pairState.value.pair}
            />
          </>
        ) : undefined}
        <AnimatePresence>
          {emojiEditOpen && pairState.value?.pair ? (
            <UploadPremiumEmojiDrawer
              open
              onOpenChange={setEditEmojiOpen}
              pair={pairState.value.pair}
            />
          ) : undefined}
        </AnimatePresence>
      </Overflow>
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  grid-template-rows: auto auto minmax(0, 1fr);
  background: var(--background-1);
`

const Overflow = styled(FadeOverflow)`
  display: grid;
  gap: 9px;

  height: 100%;
  min-height: 0;
  overflow-y: auto;
`
